import { useEffect } from "react";
import { useLocation, useMatches } from "react-router";

import type { SiteContent } from "@/content";
import { org } from "@/data/site";
import { applyPageMeta } from "@/lib/page-meta";
import { robotsDirective } from "@/lib/site-url";
import { isPageData, type PageData } from "@/page-data";
import type { Metadata } from "@/types/metadata";

/**
 * Keeps the document title and canonical link correct across client navigation.
 *
 * The server writes the head for the URL that was requested, which is what
 * crawlers and link-preview bots read and is the whole point of rendering on the
 * server. But once the router takes over, moving between pages changes the DOM
 * and nothing else: the tab still shows the title of whatever page the visitor
 * landed on first, a bookmark saves the wrong name, and a screen reader
 * announces the wrong page on every navigation.
 *
 * This runs in the browser only. It reuses each route's own `meta` function and
 * the same page overrides the server applies, so there is one definition of a
 * page's title rather than two that can drift.
 */
type MetaHandle = {
  meta?: (args: { params: Record<string, string | undefined>; data?: unknown }) => Metadata | null;
};

export function useDocumentMeta(content: SiteContent) {
  const matches = useMatches();
  const location = useLocation();

  useEffect(() => {
    let resolved: Metadata | null = null;
    let pageData: PageData | null = null;
    for (const match of matches) {
      const handle = match.handle as MetaHandle | undefined;
      const meta = handle?.meta?.({ params: match.params, data: match.data });
      // Deepest match with metadata wins, so a detail page beats its parent.
      if (meta) resolved = meta;
      if (isPageData(match.data)) pageData = match.data;
    }
    if (pageData) {
      resolved = applyPageMeta(resolved, {
        page: pageData.page,
        pathname: location.pathname,
        media: content.media,
        titleIsComplete: pageData.slug === "home",
      });
    }
    if (!resolved) return;

    document.title = resolved.titleIsComplete ? resolved.title : `${resolved.title} | ${org.shortName}`;

    const canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (canonical) canonical.href = resolved.alternates.canonical;

    const description = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (description) description.content = resolved.description;

    const robots = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
    if (robots) robots.content = robotsDirective === "index, follow" && resolved.robots ? resolved.robots : robotsDirective;
  }, [content.media, location.pathname, matches]);
}
